import type { ReactNode } from "react";

import { Instagram, Linkedin, Youtube } from "lucide-react";
import { Link } from "@/lib/router";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { socialLinks, type SocialId } from "@/constants/site";

/** The X logo (lucide has no icon for it). */
function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden className={className} fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

const ICONS: Record<SocialId, (props: { className?: string }) => ReactNode> = {
  instagram: (p) => <Instagram {...p} />,
  x: (p) => <XLogo {...p} />,
  youtube: (p) => <Youtube {...p} />,
  linkedin: (p) => <Linkedin {...p} />,
};

/** Round links to the official social accounts. */
export function SocialLinks({ className, english = false }: { className?: string; english?: boolean }) {
  const t = useT();
  return (
    <ul className={cn("flex flex-wrap gap-2", className)}>
      {socialLinks.map((s) => {
        const Icon = ICONS[s.id];
        return (
          <li key={s.id}>
            <a
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={english ? `Prakrit Sarita on ${s.label}` : t(`${s.label} पर प्राकृत सरिता`, `Prakrit Sarita on ${s.label}`)}
              title={s.label}
              className="grid size-11 place-items-center rounded-full bg-card text-primary shadow-[var(--shadow-card)] transition hover:-translate-y-0.5 hover:bg-primary hover:text-primary-foreground"
            >
              <Icon className="size-5" />
            </a>
          </li>
        );
      })}
    </ul>
  );
}

type FooterLink = { to: string; hi: string; en: string };

const COLUMNS: { hi: string; en: string; links: FooterLink[] }[] = [
  {
    hi: "सीखें",
    en: "Learn",
    links: [
      { to: "/lesson", hi: "पाठ", en: "Lessons" },
      { to: "/grammar", hi: "व्याकरण", en: "Grammar" },
      { to: "/dictionary", hi: "शब्दकोश", en: "Dictionary" },
      { to: "/stories", hi: "कथा-वाटिका", en: "Stories" },
      { to: "/games", hi: "खेल", en: "Games" },
    ],
  },
  {
    hi: "जानकारी",
    en: "About",
    links: [
      { to: "/about", hi: "परिचय", en: "About us" },
      { to: "/curriculum", hi: "पाठ्यक्रम", en: "Curriculum" },
      { to: "/study-plan", hi: "12 सप्ताह का पथ", en: "12-week study path" },
      { to: "/faq", hi: "सामान्य प्रश्न", en: "FAQ" },
      { to: "/contact", hi: "संपर्क", en: "Contact" },
    ],
  },
  {
    hi: "नीतियाँ",
    en: "Policies",
    links: [
      { to: "/privacy", hi: "गोपनीयता नीति", en: "Privacy policy" },
      { to: "/terms", hi: "उपयोग की शर्तें", en: "Terms of use" },
      { to: "/disclaimer", hi: "अस्वीकरण", en: "Disclaimer" },
      { to: "/accessibility", hi: "सुलभता", en: "Accessibility" },
    ],
  },
];

/** Site-wide footer. `english` keeps it English on the English-only landing page. */
export function SiteFooter({ english = false, className }: { english?: boolean; className?: string }) {
  const t = useT();
  const tr = (hi: string, en: string) => (english ? en : t(hi, en));
  return (
    <footer className={cn("mx-auto w-full max-w-6xl px-4 sm:px-6", className)}>
      <div className="bubble grid gap-8 p-6 sm:p-8 md:grid-cols-[1.4fr_repeat(3,1fr)]">
        <div>
          <Link to="/" className="inline-flex items-center gap-2" aria-label={tr("प्राकृत सरिता — मुखपृष्ठ", "Prakrit Sarita — home")}>
            <img src="/prakrit-sarita-icon.png" alt="" width={40} height={40} className="size-10" />
            <span className="deva font-display text-lg font-extrabold">{tr("प्राकृत सरिता", "Prakrit Sarita")}</span>
          </Link>
          <p className="deva mt-3 max-w-xs text-sm text-muted-foreground">
            {tr(
              "हिन्दी माध्यम से प्राकृत भाषा सीखने का खेल-भरा, स्वतंत्र अध्ययन मंच।",
              "A playful, independent place to learn Prakrit through Hindi.",
            )}
          </p>
          <p className="deva mt-4 text-sm font-bold">{tr("हमसे जुड़ें", "Follow us")}</p>
          <SocialLinks english={english} className="mt-2" />
        </div>
        {COLUMNS.map((col) => (
          <nav key={col.en} aria-label={tr(col.hi, col.en)}>
            <h2 className="deva text-sm font-extrabold text-primary">{tr(col.hi, col.en)}</h2>
            <ul className="mt-3 space-y-2">
              {col.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="deva text-sm font-semibold text-foreground/80 transition hover:text-primary">
                    {tr(l.hi, l.en)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </div>
      <p className="deva py-5 text-center text-xs font-semibold text-muted-foreground">
        {tr("प्राकृत सरिता · स्वतंत्र अध्ययन मंच", "Prakrit Sarita · an independent learning platform")}
      </p>
    </footer>
  );
}
